import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ON_DEMAND_PRODUCT } from 'constants/products.js';
import useOrgId from 'hooks/useOrgId.js';
import { useAlert } from 'contexts/AlertContext.jsx';
import useAddMediaAssetMutation from './useAddMediaAssetMutation.js';
import usePatchMediaAssetMutation from './usePatchMediaAssetMutation.js';

let fileId = 0;
const nextFileId = () => {
  fileId += 1;
  return `file_${fileId}`;
};

// Create the context
export const VideoUploadContext = createContext({
  uploadingVideos: null,
  uploadVideo: () => {},
  updateProcessingProgress: () => {},
  handleProcessingVideo: () => {},
});

// Create the provider
export const VideoUploadContextProvider = ({ children }) => {
  const orgId = useOrgId();
  const { setAlert } = useAlert();
  const history = useHistory();
  const [uploadingVideos, setUploadingVideos] = useState({});

  const hasUploadingVideos = Object.values(uploadingVideos).some((video) => {
    return Object.values(video).some(({ state }) => state === 'uploading');
  });

  const fileObjectToFileIdMap = useRef(new Map());
  const uploadIdToFileIdMap = useRef(new Map());
  const fileIdToVideoIdMap = useRef(new Map());

  const idForFile = (file) => {
    if (!fileObjectToFileIdMap.current.get(file)) {
      fileObjectToFileIdMap.current.set(file, nextFileId());
    }
    return fileObjectToFileIdMap.current.get(file);
  };

  const cleanupFile = (file) => {
    fileObjectToFileIdMap.current.delete(file);
  };

  useEffect(() => {
    if (!orgId) return;
    const showVideosUploadingAlert = () => {
      setAlert({
        title: 'Upload is in progress',
        variant: 'warning',
      });
    };

    const cleanupBlock = history.block((location) => {
      // it's always OK to navigate on pages belonging to the On demand product
      if (location.pathname.includes(`/${ON_DEMAND_PRODUCT}/`) || !hasUploadingVideos) {
        return true;
      }

      showVideosUploadingAlert();
      return false;
    });

    const beforeUnloadHandler = (e) => {
      if (hasUploadingVideos) {
        e.preventDefault();
        setAlert({
          title: 'Upload is in progress',
          variant: 'warning',
        });
      }
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      cleanupBlock();
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [history, orgId, setAlert, hasUploadingVideos]);

  const setUploadingVideoState = (videoId, fileId, newState) => {
    setUploadingVideos((prevUploadingVideos) => {
      const videoInfo = { ...prevUploadingVideos[videoId] || {} };
      const prevFileInfo = videoInfo[fileId] || {};
      const { mediaAssetId: prevMediaAssetId, vendorUploadId: prevVendorUploadId } = prevFileInfo;
      const mediaAssetId = newState.mediaAssetId || prevMediaAssetId;
      const vendorUploadId = newState.vendorUploadId || prevVendorUploadId;
      const fileInfo = { ...newState, ...{ mediaAssetId, vendorUploadId } };
      videoInfo[fileId] = fileInfo;
      return {
        ...prevUploadingVideos,
        [videoId]: videoInfo,
      };
    });
  };

  const onUploadProgress = (file, didStart, uploadedSize, totalSize, mediaAssetId) => {
    const fileId = idForFile(file);
    const videoId = fileIdToVideoIdMap.current.get(fileId);
    const progress = (didStart && totalSize > 0) ? Math.floor(100 * (uploadedSize / totalSize / 2)) : 0;
    if (videoId) {
      setUploadingVideoState(videoId, fileId, { state: 'uploading', progress, mediaAssetId });
    }
  };

  const onUploadError = (error, file) => {
    const fileId = idForFile(file);
    cleanupFile(file);

    const videoId = fileIdToVideoIdMap.current.get(fileId);
    if (videoId) {
      setUploadingVideoState(videoId, fileId, { error });
      setAlert({
        title: 'Upload failed',
        variant: 'warning',
      });
    }
  };

  const onSetUploadIdSuccess = ({ vendorUploadId }) => {
    const fileId = uploadIdToFileIdMap.current.get(vendorUploadId);
    if (!fileId) return;
    const videoId = fileIdToVideoIdMap.current.get(fileId);
    if (!videoId) return;
    setUploadingVideoState(videoId, fileId, { state: 'processing', progress: 50, vendorUploadId });
  };

  const onSetUploadIdError = (r) => {
    console.log(r);
  };

  const setMediaAssetUploadIdMutation = usePatchMediaAssetMutation({ onSuccess: onSetUploadIdSuccess, onError: onSetUploadIdError });
  const onUploadSuccess = ({ file, uploadId, mediaAssetId }) => {
    const fileId = idForFile(file);
    cleanupFile(file);

    const videoId = fileIdToVideoIdMap.current.get(fileId);
    if (videoId) {
      setUploadingVideoState(videoId, fileId, { state: 'uploading', progress: 50, vendorUploadId: uploadId });
      uploadIdToFileIdMap.current.set(uploadId, fileId);
      setMediaAssetUploadIdMutation.mutateAsync({ mediaAssetId, data: { vendorUploadId: uploadId } });
    }
  };

  const isAborted = () => false;
  const addMediaAssetMutation = useAddMediaAssetMutation({ onProgress: onUploadProgress, onError: onUploadError, onSuccess: onUploadSuccess, isAborted });
  const uploadVideo = ({ videoId, file, sourceUrl, customData, platform, importData }) => {
    const fileId = idForFile(file);
    fileIdToVideoIdMap.current.set(fileId, videoId);
    setUploadingVideoState(videoId, fileId, { state: 'uploading', progress: 0 });
    addMediaAssetMutation.mutateAsync({ videoId, file, orgId, sourceUrl, customData, platform, importData });
  };

  const updateProcessingProgress = ({ videoId, vendorUploadId, progress }) => {
    const fileId = uploadIdToFileIdMap.current.get(vendorUploadId);
    if (fileId && videoId) {
      setUploadingVideoState(videoId, fileId, { state: 'processing', vendorUploadId, progress });
    }
  };

  const handleProcessingMediaAsset = ({ videoId, mediaAssetId, vendorUploadId }) => {
    // only handle incoming processing video if we do not already have it
    if (!uploadIdToFileIdMap.current.has(vendorUploadId)) {
      const fileId = nextFileId();
      fileIdToVideoIdMap.current.set(fileId, videoId);
      uploadIdToFileIdMap.current.set(vendorUploadId, fileId);
      setUploadingVideoState(videoId, fileId, { state: 'processing', progress: 50, vendorUploadId, mediaAssetId });
    }
  };

  return (
    <VideoUploadContext.Provider
      value={{
        uploadingVideos,
        uploadVideo,
        updateProcessingProgress,
        handleProcessingMediaAsset,
      }}
    >
      {children}
    </VideoUploadContext.Provider>
  );
};

export const useVideoUploads = () => useContext(VideoUploadContext);
